"use client";

import {
  Koppeling,
  ParentStudentInvite,
  SubscriptionLink
} from "@/lib/parent_student_types";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { useAppContext } from "./AppContext";
import { useAuthContext } from "./AuthContext";
import { SubscriptionStatus } from "@/lib/types";
import { usePathname } from "next/navigation";

type ParentStudentContext = {
  invitesSent: ParentStudentInvite[];
  fetchInvites: () => Promise<void>;

  links: SubscriptionLink[];
  fetchLinks: () => Promise<void>;

  pendingInvites: ParentStudentInvite[];
  incomingPendingInvites: ParentStudentInvite[];
  outboundPendingInvites: ParentStudentInvite[];

  clearParentContext: () => void;

  koppelingen: Koppeling[];
};

const ParentStudentContext = createContext<ParentStudentContext | null>(null);

export const useParentStudentContext = () => {
  const context = useContext(ParentStudentContext);
  if (context === null) {
    throw Error("No ParentStudentContext available");
  }
  return context;
};

export const ParentStudentContextProvider = ({
  children
}: PropsWithChildren) => {
  const { api } = useAppContext();
  const { userData, isLoggedIn } = useAuthContext();

  const pathname = usePathname();

  const [invitesSent, setInvitesSent] = useState<ParentStudentInvite[]>([]);
  const [links, setLinks] = useState<SubscriptionLink[]>([]);

  const fetchInvites = useCallback(async () => {
    const invites = await api.getInvites();
    setInvitesSent(invites);
  }, [api]);

  const fetchLinks = useCallback(async () => {
    const links = await api.getLinks();
    setLinks(links);
  }, [api]);

  const pendingInvites: ParentStudentInvite[] = useMemo(() => {
    return invitesSent.filter((invite) => !invite.redeemed_at);
  }, [invitesSent]);

  // Only pending links that this user did not initiate theirselves
  const incomingPendingInvites: ParentStudentInvite[] = useMemo(() => {
    return invitesSent.filter(
      (invite) => !invite.redeemed_at && invite.target_email === userData?.email
    );
  }, [userData, invitesSent]);

  const outboundPendingInvites: ParentStudentInvite[] = useMemo(() => {
    return invitesSent.filter(
      (invite) =>
        !invite.redeemed_at && invite.inviter_email === userData?.email
    );
  }, [userData, invitesSent]);

  const koppelingen = useMemo(() => {
    if (invitesSent) {
      const updatedLinks: Koppeling[] = [
        ...links.map(
          (link) =>
            ({
              student_email: link.student_email,
              student_name: link.student_name,
              subscription_status: link.subscription_status,
              subscription_id: link.subscription_id ?? null,
              chargeback: Boolean(link.chargeback)
            }) as Koppeling
        ),
        ...pendingInvites.map((invite) => {
          return {
            subscription_status: invite.subscription_status,
            subscription_id: invite.subscription_id,
            invite_token: invite.invite_token,
            expiresAt: invite.expires_at,
            chargeback: Boolean(invite.chargeback)
          } as Koppeling;
        })
      ];
      return updatedLinks;
    } else {
      return links.map(
        (link) =>
          ({
            student_email: link.student_email,
            subscription_status: link.subscription_status,
            subscription_id: link.subscription_id
          }) as Koppeling
      );
    }
  }, [pendingInvites, invitesSent, links]);

  const clearParentContext = useCallback(() => {
    setInvitesSent([]);
    setLinks([]);
  }, []);

  // ON LOAD
  useEffect(() => {
    if (
      isLoggedIn &&
      pathname !== "/account" &&
      !pathname.includes("payment/gelukt") &&
      // /payment/return only redirects (window.location.replace); the browser
      // aborts these in-flight fetches, which surfaced as 2x "Network error"
      // $exception per iDEAL payment (#64). Nothing there needs this data.
      !pathname.includes("payment/return") &&
      pathname !== "/" &&
      pathname !== "/ouders"
    ) {
      // Background prefetch: a failed/aborted fetch here is not actionable
      // and must not become an unhandled rejection (PostHog $exception).
      fetchInvites().catch(() => {});
      fetchLinks().catch(() => {});
    }
  }, [isLoggedIn, fetchInvites, fetchLinks]);

  const providerValue = useMemo(
    () => ({
      invitesSent,
      fetchInvites,
      links,
      fetchLinks,
      clearParentContext,
      pendingInvites,
      incomingPendingInvites,
      outboundPendingInvites,
      koppelingen
    }),
    [
      invitesSent,
      fetchInvites,
      links,
      fetchLinks,
      clearParentContext,
      pendingInvites,
      incomingPendingInvites,
      outboundPendingInvites,
      koppelingen
    ]
  );

  return (
    <ParentStudentContext.Provider value={providerValue}>
      {children}
    </ParentStudentContext.Provider>
  );
};
